import { useState } from 'react';
import { Link, Route, useParams, useResolvedPath } from 'react-router';
import { Button, Card, Space, Table } from 'antd';
import { useQuery } from '@apollo/client/react';

import FraudCaseDetails from './FraudCaseDetails';
import FraudNewCaseForm from './FraudNewCaseForm';
import Pill from 'components/Pill/Pill';
import QueryResult from 'components/QueryResult/QueryResult';

import { FRAUD_CASES } from 'queries/fraudCases';
import { FRAUD_STATUS } from 'utilities/constants';
import { formatDate } from 'utilities/datesAndTimes';

import { SentryRoutes } from 'services/sentry';

const FraudStatusBadge = ({ status }) => {
  let color = '';
  let displayText = '';
  switch (status) {
    case FRAUD_STATUS.SETTING_AS_CLEARED:
    case FRAUD_STATUS.CLEARED:
      color = 'green';
      displayText = FRAUD_STATUS.CLEARED;
      break;
    case FRAUD_STATUS.SETTING_AS_CONFIRMED:
    case FRAUD_STATUS.CONFIRMED:
      color = 'red';
      displayText = FRAUD_STATUS.CONFIRMED;
      break;
    case FRAUD_STATUS.SETTING_AS_UNDER_INVESTIGATION:
    case FRAUD_STATUS.UNDER_INVESTIGATION:
      color = 'orange';
      displayText = FRAUD_STATUS.UNDER_INVESTIGATION;
      break;
    case FRAUD_STATUS.SETTING_AS_RESTRICTED:
    case FRAUD_STATUS.RESTRICTED:
      color = 'orange';
      displayText = FRAUD_STATUS.RESTRICTED;
      break;
    case FRAUD_STATUS.SETTING_AS_RESTORED:
    case FRAUD_STATUS.RESTORED:
      color = 'blue';
      displayText = FRAUD_STATUS.RESTORED;
      break;
    case FRAUD_STATUS.SETTING_AS_REJECTED:
      color = 'red';
      displayText = FRAUD_STATUS.REJECTED;
    default:
  }
  return <Pill color={color}>{displayText}</Pill>;
};

const FraudCasesPage = () => {
  const [addingNewCase, setAddingNewCase] = useState(false);

  const { customerId, applicationId } = useParams();

  const { loading, error, data } = useQuery(FRAUD_CASES, {
    variables: { customerId, applicationId },
  });

  // This page is mounted under both /customers/:customerId/fraud and
  // /applications/:applicationId/fraud-cases, so resolve its own base URL
  // rather than hard-coding either one. Only sound for the links below, which
  // render on the list URL: the mount is a splat route, and React Router
  // resolves against the whole match, so on /…/fraud/case/:caseId this is the
  // case URL rather than the base. Link relative to the route instead of
  // reaching for this from a nested route.
  const { pathname: basePath } = useResolvedPath('');

  const columns = [
    {
      title: 'Case',
      dataIndex: 'type',
      key: 'type',
      render: (val, { caseHash }) => (
        <Link key={caseHash} to={`${basePath}/case/${caseHash}`}>
          {val}
        </Link>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <FraudStatusBadge status={status} />,
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      render: formatDate,
    },
    {
      title: 'Updated',
      dataIndex: 'updated',
      key: 'updated',
      render: formatDate,
    },
    {
      title: 'Action',
      dataIndex: 'details',
      key: 'action',
      render: (val) => <div>{val[0]?.action}</div>,
    },
    {
      title: 'Case #',
      dataIndex: 'caseHash',
    },
  ];

  return (
    <QueryResult loading={loading} error={error} data={data} called={true}>
      <SentryRoutes>
        <Route
          path="case/:caseId"
          element={<FraudCaseDetails cases={data?.fraudCasesV2} />}
        />
        <Route
          path="*"
          element={
            addingNewCase ? (
              <FraudNewCaseForm setAddingNewCase={setAddingNewCase} />
            ) : (
              <Card title="Fraud Cases">
                <Space orientation="vertical" style={{ width: '100%' }}>
                  <Table
                    columns={columns}
                    dataSource={data?.fraudCasesV2}
                    bordered={true}
                    size="small"
                    pagination={false}
                    rowKey="caseHash"
                  />
                  <Button
                    data-testid="new-case-button"
                    onClick={() => setAddingNewCase(true)}
                  >
                    New Case
                  </Button>
                </Space>
              </Card>
            )
          }
        />
      </SentryRoutes>
    </QueryResult>
  );
};

export default FraudCasesPage;
